/**
 * Orchestra AI Platform - Scenario Card Component
 * 
 * Card component for displaying educational scenarios
 * 
 * @fileoverview Scenario card with metadata and actions
 */

import PropTypes from 'prop-types';
import { ServerBadge } from '../ui/ServerBadge';
import './ScenarioCard.css';

/**
 * @typedef {Object} EducationalScenario
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string} category
 * @property {string} difficulty
 * @property {number} estimated_time
 * @property {string[]} servers_used
 * @property {string[]} use_cases
 */

/**
 * Scenario Card Component
 * @param {Object} props
 * @param {EducationalScenario} props.scenario - Scenario data
 * @param {Function} props.onUse - Use scenario handler
 * @param {Function} [props.onView] - View details handler
 */
export function ScenarioCard({ scenario, onUse, onView }) {
  const categoryLabels = {
    homework_assistance: 'Homework',
    language_learning: 'Language',
    cross_cultural: 'Cultural',
    assessment: 'Assessment',
    content_creation: 'Content',
    adaptive_learning: 'Adaptive',
    visual_learning: 'Visual',
  };

  return (
    <div className="scenario-card">
      {/* Header */}
      <div className="card-header">
        <h3 className="card-title">{scenario.name}</h3>
        <span className={`difficulty-badge ${scenario.difficulty}`}>
          {scenario.difficulty}
        </span>
      </div>

      {/* Description */}
      <p className="card-description">{scenario.description}</p>

      {/* Metadata */}
      <div className="card-metadata">
        <span className="category-badge">
          {categoryLabels[scenario.category] || scenario.category}
        </span>
        <span>⏱ {scenario.estimated_time} min</span>
        <span>{scenario.servers_used.length} server(s)</span>
      </div>

      {/* Servers */}
      <div className="servers-container">
        {scenario.servers_used.map((server) => (
          <ServerBadge key={server} server={server} size="sm" />
        ))}
      </div>

      {/* Use Cases */}
      {scenario.use_cases && scenario.use_cases.length > 0 && (
        <div className="use-cases-container">
          <p className="use-cases-label">Use Cases:</p>
          <div className="use-cases-list">
            {scenario.use_cases.slice(0, 2).map((useCase, index) => (
              <span key={index} className="use-case-badge">
                {useCase}
              </span>
            ))}
            {scenario.use_cases.length > 2 && (
              <span className="use-case-more">
                +{scenario.use_cases.length - 2} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="card-actions">
        <button onClick={onUse} className="action-button primary">
          Use Template
        </button>
        {onView && (
          <button onClick={onView} className="action-button secondary">
            View Details
          </button>
        )}
      </div>
    </div>
  );
}

ScenarioCard.propTypes = {
  scenario: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    difficulty: PropTypes.oneOf(['beginner', 'intermediate', 'advanced']).isRequired,
    estimated_time: PropTypes.number.isRequired,
    servers_used: PropTypes.arrayOf(PropTypes.string).isRequired,
    use_cases: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onUse: PropTypes.func.isRequired,
  onView: PropTypes.func,
};

